/**
 * sqlite3mt sync-engine orchestration
 *
 * §96 §14.1 decision hub, §14.2 push orchestration, §15.9 reconcile
 * orchestration contracts, and R66/I41 post-busy-epilog pattern.
 *
 * This module defines:
 * 1. ControlWorkerRuntimeState — the combined mutable state type that merges
 *    DB-view, workspace-view, WAL integration, volatile busy flags,
 *    remote tracking, fatal state, and staged blobs.
 * 2. maybeStartPushOrImport() — the normative decision function (§14.1)
 *    that routes to push or remote reconcile.
 * 3. runPush() — the full §14.2 push async state machine.
 * 4. Post-busy-epilog helper — R66's requirement that no non-fatal exit
 *    path may skip the restart kick.
 * 5. Async orchestration interfaces — hooks and contracts for
 *    runRemoteReconcile().
 */

import type { PendingBaseAdvance, DurableReconcileIntent } from './bootstrap'
import { BASE_ADVANCE_FINALIZATION_FAILED } from './commit-registration'
import { subtractPublishedSnapshotFromLiveWorkspaceState, workspaceHasLocalDelta } from './dbview'
import type { ResidualOverlay } from './merge-plan'
import {
    capturePushSnapshot,
    INTERNAL_PENDING_BASE_ADVANCE_REENTRY,
    normalizedLocalDeltaIsEmpty,
    revalidatePushPostPublishLocalBaseOrReturn,
    revalidatePushPrePublishBaseOrReturn,
    type PushSnapshot,
} from './push'
import {
    buildImportDeltaFromPlan,
    reconcileEffectiveIntegratedFrameMax,
    RECONCILE_ORPHANED_UNINTEGRATED_WAL_TAIL,
    RECONCILE_STUCK_OUTSTANDING_COMMIT_POST,
} from './reconcile'
import {
    isNonFatalSyncError,
    normalizeSyncError,
} from './snapshot-persistence'
import type { ApplyRemoteHeadResult } from './remote-head'
import { emitSdkEvent } from '../observers'
import { sdkConsole } from '../logger'
import type {
    AppendWalFramesMeta,
    CheckpointMode,
    ImportTryResult,
    MergePlan,
    PageHash,
    Pgno,
    PublicHead,
    Root,
    Sqlite3MtDebugStagePayload,
    WalSnapshot,
} from './types'
import { sameRoot } from './types'

// ── §14.1 Decision result ─────────────────────────────────────

/** Possible outcomes of maybeStartPushOrImport(). */
export type MaybeStartPushOrImportDecision =
    | { kind: 'IDLE' }
    | { kind: 'START_PUSH' }
    | { kind: 'START_RECONCILE' }

/**
 * Explicit lifecycle entrypoints that can request a sync-engine pass.
 *
 * The concrete lifecycle/runtime bridge still delegates execution to an
 * external kick callback today, but the next integration slice needs these
 * trigger sites enumerated so lifecycle-owned scheduling can be wired
 * without conflating bootstrap refreshes, commit-driven pushes, remote-head
 * updates, checkpoint follow-ups, and retry wakeups.
 */
export type SyncEngineKickReason =
    | 'bootstrap-remote-refresh'
    | 'local-commit'
    | 'checkpoint-done'
    | 'remote-head-update'
    | 'external-client-idle'
    | 'sync-retry'
    | 'adapter-start-sync'
    | 'adapter-sync-now'
    | 'adapter-flush-local'
    | 'adapter-flush-remote'

// ── §14.1/§15.9 Decision guards ────────────────────────────────

/**
 * Common read-only guard subset needed by the decision function.
 *
 * Extracted so that the decision function can operate on any object
 * that provides these fields — the full runtime state, a snapshot for
 * testing, or a mock.
 */
export interface SyncDecisionState {
    syncFatalError: unknown | null
    pushBusy: boolean
    mergeBusy: boolean
    checkpointBusy: boolean
    checkpointQuiescentBusy: boolean
    checkpointWanted: boolean
    externalClientRequestCount?: number
    pendingBaseAdvance: PendingBaseAdvance | null
    reconcileIntent: DurableReconcileIntent | null
    remoteHead: PublicHead
    workspaceBaseHead: PublicHead
    workspaceOverlay: ReadonlyMap<Pgno, PageHash>
    workspacePageCount: number
}

// ── §14.1 maybeStartPushOrImport ────────────────────────────────

/**
 * Normative sync decision function — §14.1.
 *
 * Determines whether the sync engine should start a push, start a remote
 * reconcile, or remain idle.
 *
 * Rules (from §14.1):
 * 1. If fatal, do nothing.
 * 2. If push/merge/checkpoint busy, do nothing.
 * 3. If a checkpoint is wanted, let the sync driver launch it before remote I/O.
 * 4. If pending base advance or reconcile intent in progress, do nothing.
 * 5. If remote head differs from workspace base → reconcile.
 * 6. If workspace has local delta (overlay or page-count-only) → push.
 * 7. Otherwise idle.
 */
export function maybeStartPushOrImport(
    state: SyncDecisionState
): MaybeStartPushOrImportDecision {
    if (state.syncFatalError != null) return { kind: 'IDLE' }
    if (state.pushBusy || state.mergeBusy) return { kind: 'IDLE' }
    if ((state.externalClientRequestCount ?? 0) > 0) return { kind: 'IDLE' }
    if (state.checkpointBusy || state.checkpointQuiescentBusy) return { kind: 'IDLE' }
    if (state.checkpointWanted) return { kind: 'IDLE' }
    if (state.pendingBaseAdvance != null) return { kind: 'IDLE' }
    if (state.reconcileIntent != null) return { kind: 'IDLE' }

    // §14.1: remote head differs from local base → reconcile
    if (
        !sameRoot(state.remoteHead.root, state.workspaceBaseHead.root)
        || state.remoteHead.version !== state.workspaceBaseHead.version
    ) {
        return { kind: 'START_RECONCILE' }
    }

    // §14.1 / R49: workspaceHasLocalDelta checks overlay.size > 0
    // OR page-count-only change
    if (workspaceHasLocalDelta(
        state.workspaceOverlay,
        state.workspacePageCount,
        state.workspaceBaseHead.root.meta.pageCount
    )) {
        return { kind: 'START_PUSH' }
    }

    return { kind: 'IDLE' }
}

// ── R66 / I41 Post-busy-epilog ──────────────────────────────────

/**
 * Post-busy sync restart decision — R66, I41.
 *
 * After pushBusy or mergeBusy is cleared, the exit path must fall
 * through to a common epilog that decides whether to restart the
 * sync engine. No non-fatal exit path may skip this check.
 *
 * Returns true if maybeStartPushOrImport() should be called.
 */
export function shouldRestartSyncPostBusy(
    syncFatalError: unknown | null,
    restartNow: boolean
): boolean {
    return restartNow && syncFatalError == null
}

type DebugStageKind = 'push' | 'reconcile'
type PushAbortReason =
    | 'revalidate-pre-materialize'
    | 'revalidate-pre-manifest-durable'
    | 'revalidate-pre-blob-upload'
    | 'revalidate-pre-manifest-upload'
    | 'revalidate-pre-cas'
    | 'revalidate-post-publish'

function emitBrowserDebugStage(payload: Sqlite3MtDebugStagePayload): void {
    const globalScope = globalThis as typeof globalThis & {
        __sqlite3mtEmitDebugStage?: (payload: Sqlite3MtDebugStagePayload) => void
    }
    globalScope.__sqlite3mtEmitDebugStage?.(payload)
}

function recordDebugStage(
    state: ControlWorkerRuntimeState,
    kind: DebugStageKind,
    stage: string | null,
): void {
    const debugState = state as ControlWorkerRuntimeState & {
        __debugPushStage?: string | null
        __debugReconcileStage?: string | null
        __debugStageTrace?: Array<{
            kind: DebugStageKind
            stage: string | null
            remoteVersion: string
            localVersion: string
        }>
    }

    if (kind === 'push') {
        debugState.__debugPushStage = stage
    } else {
        debugState.__debugReconcileStage = stage
    }

    const trace = debugState.__debugStageTrace ?? []
    trace.push({
        kind,
        stage,
        remoteVersion: state.remoteHead.version.toString(),
        localVersion: state.workspaceBaseHead.version.toString(),
    })
    if (trace.length > 32) {
        trace.splice(0, trace.length - 32)
    }
    debugState.__debugStageTrace = trace

    sdkConsole.trace('[sqlite3mt:engine] stage', {
        kind,
        stage,
        remoteVersion: state.remoteHead.version.toString(),
        localVersion: state.workspaceBaseHead.version.toString(),
        overlaySize: state.workspaceOverlay.size,
    })
    emitSdkEvent(state.sdkObservers, {
        domain: 'sync',
        level: 'debug',
        message: 'sqlite3mt sync stage changed.',
        data: {
            kind,
            stage,
            remoteVersion: state.remoteHead.version.toString(),
            localVersion: state.workspaceBaseHead.version.toString(),
            overlaySize: state.workspaceOverlay.size,
        },
    })
    emitBrowserDebugStage({
        kind,
        stage,
        remoteVersion: state.remoteHead.version.toString(),
        localVersion: state.workspaceBaseHead.version.toString(),
        overlaySize: state.workspaceOverlay.size,
    })
}

function recordPushAbort(
    state: ControlWorkerRuntimeState,
    snap: PushSnapshot,
    reason: PushAbortReason,
    afterRemoteUpload: boolean,
): void {
    const entry = {
        at: Date.now(),
        reason,
        afterRemoteUpload,
        expectedRootId: snap.expectedRoot.rootId,
        expectedVersion: snap.expectedVersion.toString(),
        workspaceBaseRootId: state.workspaceBaseHead.root.rootId,
        workspaceBaseVersion: state.workspaceBaseHead.version.toString(),
        remoteRootId: state.remoteHead.root.rootId,
        remoteVersion: state.remoteHead.version.toString(),
        workspaceBaseMatchesSnapshot:
            sameRoot(state.workspaceBaseHead.root, snap.expectedRoot)
            && state.workspaceBaseHead.version === snap.expectedVersion,
        remoteHeadMatchesSnapshot:
            sameRoot(state.remoteHead.root, snap.expectedRoot)
            && state.remoteHead.version === snap.expectedVersion,
        overlaySnapshotSize: snap.overlaySnapshot.size,
        liveOverlaySize: state.workspaceOverlay.size,
        expectedPageCount: snap.expectedPageCount,
        workspacePageCount: state.workspacePageCount,
        workspaceBasePageCount: state.workspaceBaseHead.root.meta.pageCount,
        syncRetryScheduled: state.syncRetryScheduled,
    }
    const debugState = state as ControlWorkerRuntimeState & {
        __debugPushAbortTrace?: Array<typeof entry>
    }
    const trace = debugState.__debugPushAbortTrace ?? []
    trace.push(entry)
    if (trace.length > 16) {
        trace.splice(0, trace.length - 16)
    }
    debugState.__debugPushAbortTrace = trace

    if (afterRemoteUpload) {
        sdkConsole.warn('[sqlite3mt:engine] push abort before publish', entry)
    } else {
        sdkConsole.trace('[sqlite3mt:engine] push abort before publish', entry)
    }
    emitSdkEvent(state.sdkObservers, {
        domain: 'sync',
        level: afterRemoteUpload ? 'warn' : 'debug',
        message: 'sqlite3mt push aborted before publish.',
        data: entry,
    })
}

function revalidatePushOrRecordAbort(
    state: ControlWorkerRuntimeState,
    snap: PushSnapshot,
    reason: PushAbortReason,
    afterRemoteUpload: boolean,
): boolean {
    const ok = revalidatePushPrePublishBaseOrReturn(
        snap,
        state.workspaceBaseHead,
        state.workspacePageCount,
        state.remoteHead
    )
    if (!ok) {
        recordPushAbort(state, snap, reason, afterRemoteUpload)
    }
    return ok
}

// ── Async orchestration interfaces ─────────────────────────────

/**
 * Hooks that the sync-engine async orchestrations (runPush, runRemoteReconcile)
 * call back into. These are provided by the Control Worker runtime.
 *
 * Design intent: by defining these as interfaces, the orchestrations
 * can be tested in isolation with mock hooks.
 */
export interface SyncEngineHooks {
    /** Notify sync engine that state may have changed. Calls maybeStartPushOrImport. */
    maybeStartPushOrImport(): void
    /** Enter fatal runtime state. */
    enterFatalRuntime(error: unknown, reason: string): void
    /** Schedule a transient sync retry after delay. */
    scheduleSyncRetry(delayMs: number): void
    /** Request a checkpoint after successful push or reconcile. */
    maybeRequestCheckpoint(reason: string): void
    /** Request compaction of dbBaseRoot overlay. */
    maybeCompactDbBaseRoot(): void
}

/**
 * Combined mutable Control Worker runtime state — §5 + §9 + §13 + §19.
 *
 * This type merges:
 * - ControlWorkerState (DB-view + workspace + WAL) from types.ts
 * - FreshGenerationVolatileState (busy flags, intents) from bootstrap.ts
 * - remoteHead tracking state from remote-head.ts
 * - syncFatalError and runtimeEpoch
 * - staged blob/manifest-node stores
 *
 * The actual async orchestrations (runPush, runRemoteReconcile) operate
 * on this combined state through the hooks pattern above, ensuring
 * that every mutation goes through the correct owner (Control Worker).
 */
export interface ControlWorkerRuntimeState {
    // ── DB-view (§1.2, §9) ────────────────────────────────────
    dbBaseRoot: import('./types').Root
    dbOverlay: Map<import('./types').Pgno, import('./types').PageHash>
    dbPageCount: number

    // ── Workspace-view (§1.2) ─────────────────────────────────
    workspaceBaseHead: PublicHead
    workspaceOverlay: Map<import('./types').Pgno, import('./types').PageHash>
    workspacePageCount: number

    // ── WAL integration (§1.4, §9) ────────────────────────────
    pendingDbDeltas: Map<import('./types').FrameMax, import('./types').Delta>
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
    lastWalSnapshot: import('./types').WalSnapshot | null
    localCommitSeq: bigint

    // ── Remote tracking (§13) ─────────────────────────────────
    remoteHead: PublicHead
    remoteRefreshReqSeq: bigint
    remoteRefreshAppliedReqSeq: bigint

    // ── Sync state (§14, §15) ─────────────────────────────────
    syncFatalError: unknown | null
    lastSyncError?: unknown | null
    runtimeEpoch: bigint

    // ── Busy flags (§5, §14, §15) ─────────────────────────────
    pushBusy: boolean
    mergeBusy: boolean
    checkpointBusy: boolean
    checkpointQuiescentBusy: boolean
    compactBusy: boolean
    compactWanted: boolean

    // ── Checkpoint state (§16) ────────────────────────────────
    checkpointWanted: boolean
    checkpointWantedMode: CheckpointMode | null
    checkpointInflightSeq: number
    checkpointNoProgressCount: number

    // ── Base-advance barrier (§14, §15) ───────────────────────
    pendingBaseAdvance: PendingBaseAdvance | null
    reconcileIntent: DurableReconcileIntent | null
    inflightCommitRegistrations: number
    headAdvancePreparing: boolean
    headAdvancePrepareWaiter: import('./bootstrap').Deferred<void> | null
    commitRegistrationDrainWaiter: import('./bootstrap').Deferred<void> | null

    // ── Snapshot persistence (§19.1) ──────────────────────────
    snapshotPersistTail: Promise<void>
    snapshotPersistBusy: boolean
    snapshotPersistWanted: boolean

    // ── Retry (§19.2) ────────────────────────────────────────
    syncRetryScheduled: boolean

    // ── Staged stores (§14.2) ─────────────────────────────────
    stagedCas: Map<string, Uint8Array>
    stagedManifestNodes: Map<string, Uint8Array>

    // ── Push snapshot (§14.1) ─────────────────────────────────
    pushSnap: import('./push').PushSnapshot | null

    // ── Configuration ─────────────────────────────────────────
    syncRetryDelayMs: number
    sdkObservers?: import('../observers').SdkObservers
}

// ── Guard predicates for async orchestrations ──────────────────

/**
 * Check if the sync engine is blocked from starting new work.
 *
 * Used as a pre-guard by both runPush() and runRemoteReconcile().
 */
export function syncEngineIsBlocked(state: SyncDecisionState): boolean {
    return (
        state.syncFatalError != null
        || state.pushBusy
        || state.mergeBusy
        || state.pendingBaseAdvance != null
        || state.reconcileIntent != null
    )
}

/**
 * Check if reconcile is the correct next action.
 *
 * §14.1: remote head must differ from workspace base head.
 */
export function shouldReconcile(
    remoteHead: PublicHead,
    workspaceBaseHead: PublicHead
): boolean {
    return (
        !sameRoot(remoteHead.root, workspaceBaseHead.root)
        || remoteHead.version !== workspaceBaseHead.version
    )
}

/**
 * Check if workspace base matches remote head exactly.
 *
 * Used by reconcile to decide whether to fall through to push instead.
 */
export function workspaceBaseMatchesRemote(
    remoteHead: PublicHead,
    workspaceBaseHead: PublicHead
): boolean {
    return (
        sameRoot(remoteHead.root, workspaceBaseHead.root)
        && remoteHead.version === workspaceBaseHead.version
    )
}

// ── §14.2 runPush hooks interface ─────────────────────────────

/**
 * Hooks for the §14.2 push orchestration.
 *
 * All I/O, transport, and complex external operations are injected
 * through this interface so that runPush() can be tested in isolation
 * with deterministic mocks.
 */
export interface RunPushHooks {
    // ── Manifest operations (§12) ────────────────────────────
    normalizeSparseOverlay(
        baseRoot: Root,
        overlay: ReadonlyMap<Pgno, PageHash>,
        pageCount: number
    ): Promise<Map<Pgno, PageHash>>

    materializeRoot(
        baseRoot: Root,
        overlay: Map<Pgno, PageHash>,
        pageCount: number,
        parentPublicRoot: Root
    ): Promise<{ root: Root; manifestNodeBlobsByHash: Map<string, Uint8Array> }>

    collectUploadSet(
        baseRoot: Root,
        targetRoot: Root
    ): { pageBlobHashes: Set<PageHash>; manifestNodeHashes: Set<string> }

    // ── Transport (§12/§97) ──────────────────────────────────
    rpcPublishIfCurrent(
        expectedRoot: Root,
        expectedVersion: bigint,
        nextRoot: Root,
        publishPrefetchHints?: string[]
    ): Promise<{ ok: boolean; currentRoot: Root; currentVersion: bigint }>

    rpcPutMissingBlobs(
        hashes: Iterable<PageHash>,
        loader: (h: PageHash) => Promise<Uint8Array | null>
    ): Promise<void>

    rpcPutMissingManifestNodes(
        hashes: Iterable<string>,
        loader: (h: string) => Promise<Uint8Array | null>
    ): Promise<void>

    // ── Durable store ────────────────────────────────────────
    putManifestNode(hash: string, bytes: Uint8Array): Promise<void>

    // ── Head advance barrier (§11) ───────────────────────────
    beginHeadAdvancePreparationAndDrain(): Promise<void>
    endHeadAdvancePreparation(): void

    // ── Snapshot persistence (§19) ───────────────────────────
    captureDurableSnapshot(): unknown
    persistSnapshotSerial(snapshot: unknown, truncateJournal: boolean): Promise<void>
    requestPersistLocalSnapshot(): void

    // ── Remote head tracking (§13) ───────────────────────────
    applyRemoteHeadIfNewer(root: Root, version: bigint, reqSeq: bigint): ApplyRemoteHeadResult

    // ── Post-push cleanup ────────────────────────────────────
    gcStagedIfSafe(): void

    // ── Staged blob access ───────────────────────────────────
    getStagedBlob(hash: PageHash): Uint8Array | undefined
    getStagedManifestNode(hash: string): Uint8Array | undefined
    getDurableBlob(hash: PageHash): Promise<Uint8Array | null>
    getDurableManifestNode(hash: string): Promise<Uint8Array | null>

    // ── Error handling ───────────────────────────────────────
    abortIfFatal(): void
    enterFatalRuntime(error: unknown, reason: string): void
    isTransientSyncError(error: unknown): boolean
    scheduleSyncRetry(): void

    // ── Sync engine restart (R66 epilog) ─────────────────────
    maybeStartPushOrImport(): void
    /** Let a wanted WAL checkpoint start before remote push transport work. */
    maybeRequestCheckpoint(reason: string): void | Promise<unknown>
}

const SQLITE3MT_PUBLISH_PREFETCH_HINT_LIMIT = 256

function buildPublishPrefetchHints(
    uploadSet: { pageBlobHashes: Set<PageHash>; manifestNodeHashes: Set<string> },
    nextRoot: Root,
): string[] | undefined {
    const hints: string[] = []
    const seen = new Set<string>()
    const pushHint = (hash: string | null | undefined): void => {
        if (!hash || seen.has(hash)) {
            return
        }
        if (hints.length >= SQLITE3MT_PUBLISH_PREFETCH_HINT_LIMIT) {
            return
        }
        seen.add(hash)
        hints.push(hash)
    }

    // Root manifest first so cold readers can resolve changed pages before
    // falling back to one-by-one remote page loads.
    pushHint(nextRoot.meta.manifestRootId)
    for (const hash of uploadSet.manifestNodeHashes) {
        pushHint(hash)
    }
    for (const hash of uploadSet.pageBlobHashes) {
        pushHint(hash)
    }

    return hints.length > 0 ? hints : undefined
}

// ── §15.9 Reconcile snapshot + revalidation ─────────────────────

/**
 * Snapshot of the workspace + remote state captured at the start of
 * runRemoteReconcile(). Used for revalidation across await boundaries.
 *
 * §15.9: revalidateReconcileBase checks that workspace base head,
 * workspace page count, and remote head have not changed since the
 * snapshot was taken.
 */
export interface ReconcileSnapshot {
    baseRoot: Root
    baseVersion: bigint
    basePageCount: number
    remoteRoot: Root
    remoteVersion: bigint
    wal: WalSnapshot
}

/**
 * §15.9 revalidation: verify that the reconcile snapshot is still valid.
 *
 * The reconcile orchestration must abort if the workspace base or remote
 * head has changed under it (§13 async update, §11 concurrent commit).
 *
 * Returns true if the snapshot still matches live state.
 */
export function revalidateReconcileBase(
    snap: ReconcileSnapshot,
    state: Pick<
        ControlWorkerRuntimeState,
        'workspaceBaseHead' | 'workspacePageCount' | 'remoteHead'
    >
): boolean {
    return (
        sameRoot(state.workspaceBaseHead.root, snap.baseRoot)
        && state.workspaceBaseHead.version === snap.baseVersion
        && state.workspacePageCount === snap.basePageCount
        && sameRoot(state.remoteHead.root, snap.remoteRoot)
        && state.remoteHead.version === snap.remoteVersion
    )
}

// ── §15.9 runRemoteReconcile hooks ─────────────────────────────

/**
 * Hooks for the §15.9 runRemoteReconcile orchestration.
 *
 * All I/O, transport, WAL, and complex external operations are injected
 * through this interface so that runRemoteReconcile() can be tested in
 * isolation with deterministic mocks.
 *
 * The hooks are a superset of the push hooks plus reconcile-specific
 * operations: WAL snapshot reads, merge plan building, import under
 * WAL_WRITE, catch-up loops, and intent management.
 */
export interface RunReconcileHooks {
    // ── Manifest operations (§12, §15.3) ──────────────────────
    normalizeSparseOverlay(
        baseRoot: Root,
        overlay: ReadonlyMap<Pgno, PageHash>,
        pageCount: number
    ): Promise<Map<Pgno, PageHash>>

    buildMergePlan(args: {
        baseRoot: Root
        baseVersion: bigint
        remoteRoot: Root
        remoteVersion: bigint
        wal: WalSnapshot
        localNorm: Map<Pgno, PageHash>
        localPageCount: number
        pendingDbDeltasIntegratedFrameMax: number
    }): Promise<MergePlan>

    buildResidualOverlayAgainstRemote(plan: MergePlan): ResidualOverlay
    ensureMetadataCarrierFrameIfNeeded(plan: MergePlan): Promise<void>

    // ── WAL / advanceDbHead ───────────────────────────────────
    advanceDbHead(): WalSnapshot

    // ── Reconcile import (§15.7) ──────────────────────────────
    tryImportPreparedIntentAsSingleWalCommit(args: {
        intent: DurableReconcileIntent
        plan: MergePlan
    }): Promise<ImportTryResult>

    // ── Catch-up (§15.8) ──────────────────────────────────────
    catchUpMergePlanToFrame(
        plan: MergePlan,
        targetFrameMax: number
    ): Promise<void>
    ensureReconcileIntentBlobsDurable(plan: MergePlan): Promise<void>

    // ── Reconcile tail probes ─────────────────────────────────
    reconcileTailCanStillIntegrateSoon(): boolean
    hasOutstandingCommitPosts(): boolean
    awaitOutstandingCommitPostsToChangeOrEnterFatal(reason: string): Promise<void>
    yieldOneTaskTurnForPendingCommitMessages(): Promise<void>

    // ── Reconcile orphan probe ────────────────────────────────
    reconcileOrphanProbeState(live: WalSnapshot): {
        salt0: number; salt1: number; liveMxFrame: number; integratedFrameMax: number
    }
    sameReconcileOrphanProbeState(
        a: { salt0: number; salt1: number; liveMxFrame: number; integratedFrameMax: number },
        b: { salt0: number; salt1: number; liveMxFrame: number; integratedFrameMax: number }
    ): boolean
    sameWalSalt(a0: number | null, a1: number | null, b0: number | null, b1: number | null): boolean

    // ── Durable intent management ─────────────────────────────
    nextIntentId(): bigint
    writeReconcileIntent(intent: DurableReconcileIntent): Promise<void>
    abandonPreparedReconcileIntent(intent: DurableReconcileIntent): Promise<void>

    // ── Head-advance barrier (§11) ────────────────────────────
    beginHeadAdvancePreparationAndDrain(): Promise<void>
    endHeadAdvancePreparation(): void

    /**
     * Start a pending base advance for reconcile.
     * Updates workspaceBaseHead, workspaceOverlay, workspacePageCount
     * and schedules durable snapshot persistence.
     */
    startPendingReconcileBaseAdvance(
        intent: DurableReconcileIntent,
        residual: ResidualOverlay,
        appendMeta: AppendWalFramesMeta | null
    ): void
    flushPendingBaseAdvanceIfAny(): Promise<void>

    // ── Snapshot persistence (§19) ────────────────────────────
    captureDurableSnapshot(): unknown
    persistSnapshotSerial(snapshot: unknown, truncateJournal: boolean): Promise<void>
    requestPersistLocalSnapshot(): void

    // ── Post-reconcile cleanup ────────────────────────────────
    gcStagedIfSafe(): void

    // ── Staged blob access ────────────────────────────────────
    getStagedBlob(hash: PageHash): Uint8Array | undefined
    getDurableBlob(hash: PageHash): Promise<Uint8Array | null>

    // ── Error handling ────────────────────────────────────────
    abortIfFatal(): void
    enterFatalRuntime(error: unknown, reason: string): void
    enterUncertainAppendingFatal(
        enterFatal: (error: unknown, reason: string) => void
    ): void
    isTransientSyncError(error: unknown): boolean
    scheduleSyncRetry(): void

    // ── Sync engine restart (R66 epilog) ─────────────────────
    maybeStartPushOrImport(): void
    maybeRequestCheckpoint(reason: string): void

    // ── SAB access ────────────────────────────────────────────
    getSab1(): SharedArrayBuffer
    getSab4(): SharedArrayBuffer
}

// ── §14.2 runPush ────────────────────────────────────────────

/**
 * Normative push flow — §14.2.
 *
 * State machine that:
 * 1. Captures a push snapshot
 * 2. Normalizes the overlay against the base root
 * 3. Materializes a new root if changes exist
 * 4. Persists manifest nodes and uploads blobs
 * 5. Publishes via CAS
 * 6. Advances workspace base head through the head-advance barrier
 *
 * R66 invariant: no non-fatal exit path may skip the post-busy epilog.
 * The `do { ... } while (false)` block implements the spec's
 * `goto finish_try` pattern — every `break` falls through to the
 * common finally + epilog.
 */
export async function runPush(
    state: ControlWorkerRuntimeState,
    hooks: RunPushHooks
): Promise<void> {
    const setPushStage = (stage: string | null): void => {
        recordDebugStage(state, 'push', stage)
    }

    // ── Pre-guards ───────────────────────────────────────────
    if (state.syncFatalError != null) return
    if (state.pushBusy || state.mergeBusy) return
    if (state.checkpointBusy || state.checkpointQuiescentBusy) return
    if (state.checkpointWanted) {
        await hooks.maybeRequestCheckpoint('pre-push-checkpoint')
        return
    }
    if (state.pendingBaseAdvance != null) return
    if (state.reconcileIntent != null) return
    if (
        !sameRoot(state.remoteHead.root, state.workspaceBaseHead.root)
        || state.remoteHead.version !== state.workspaceBaseHead.version
    ) {
        return
    }
    if (
        !workspaceHasLocalDelta(
            state.workspaceOverlay,
            state.workspacePageCount,
            state.workspaceBaseHead.root.meta.pageCount
        )
    ) {
        return
    }

    state.pushBusy = true
    setPushStage('capture-snapshot')
    let restartNow = true
    let syncCompleted = false

    const snap = capturePushSnapshot(
        state.workspaceBaseHead,
        state.workspacePageCount,
        state.workspaceOverlay
    )
    state.pushSnap = snap

    try {
        // do-while-false is the TypeScript equivalent of the spec's
        // goto finish_try: every `break` skips remaining steps and
        // falls through to the common finally + epilog.
        do {
            // §14.2 step 1: normalize overlay against base root
            setPushStage('normalize-overlay')
            const norm = await hooks.normalizeSparseOverlay(
                snap.expectedRoot,
                snap.overlaySnapshot,
                snap.expectedPageCount
            )
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            // §14.2 step 2: pre-publish revalidation
            setPushStage('revalidate-pre-materialize')
            if (
                !revalidatePushOrRecordAbort(
                    state,
                    snap,
                    'revalidate-pre-materialize',
                    false
                )
            ) {
                break
            }

            // §14.2 step 3: check if delta is empty after normalization
            setPushStage('check-empty-delta')
            if (
                normalizedLocalDeltaIsEmpty(
                    norm.size,
                    snap.expectedPageCount,
                    snap.expectedRoot.meta.pageCount
                )
            ) {
                // No actual changes — clean up and exit
                const workspacePageCountRef = { value: state.workspacePageCount }
                subtractPublishedSnapshotFromLiveWorkspaceState(
                    state.workspaceOverlay,
                    workspacePageCountRef,
                    snap.overlaySnapshot,
                    snap.expectedPageCount
                )
                state.workspacePageCount = workspacePageCountRef.value
                hooks.requestPersistLocalSnapshot()
                syncCompleted = true
                break
            }

            // §14.2 step 4: materialize new root
            setPushStage('materialize-root')
            const mat = await hooks.materializeRoot(
                snap.expectedRoot,
                norm,
                snap.expectedPageCount,
                snap.expectedRoot
            )
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            // §14.2 step 5: revalidate
            setPushStage('revalidate-pre-manifest-durable')
            if (
                !revalidatePushOrRecordAbort(
                    state,
                    snap,
                    'revalidate-pre-manifest-durable',
                    false
                )
            ) {
                break
            }

            // §14.2 step 6: persist manifest nodes to durable store
            setPushStage('persist-manifest-durable')
            for (const [h, b] of mat.manifestNodeBlobsByHash) {
                state.stagedManifestNodes.set(h, b)
                await hooks.putManifestNode(h, b)
                hooks.abortIfFatal()
                if (state.syncFatalError != null) break
            }
            if (state.syncFatalError != null) break

            // §14.2 step 7: revalidate
            setPushStage('revalidate-pre-blob-upload')
            if (
                !revalidatePushOrRecordAbort(
                    state,
                    snap,
                    'revalidate-pre-blob-upload',
                    false
                )
            ) {
                break
            }

            // §14.2 step 8: upload page blobs
            const uploadSet = hooks.collectUploadSet(snap.expectedRoot, mat.root)
            setPushStage('upload-page-blobs')
            await hooks.rpcPutMissingBlobs(
                uploadSet.pageBlobHashes,
                async (h) =>
                    hooks.getStagedBlob(h) ?? (await hooks.getDurableBlob(h))
            )
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            // §14.2 step 9: revalidate
            setPushStage('revalidate-pre-manifest-upload')
            if (
                !revalidatePushOrRecordAbort(
                    state,
                    snap,
                    'revalidate-pre-manifest-upload',
                    true
                )
            ) {
                break
            }

            // §14.2 step 10: upload manifest nodes
            setPushStage('upload-manifest-nodes')
            await hooks.rpcPutMissingManifestNodes(
                uploadSet.manifestNodeHashes,
                async (h) =>
                    hooks.getStagedManifestNode(h)
                    ?? (await hooks.getDurableManifestNode(h))
            )
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            // §14.2 step 11: revalidate
            setPushStage('revalidate-pre-cas')
            if (
                !revalidatePushOrRecordAbort(
                    state,
                    snap,
                    'revalidate-pre-cas',
                    true
                )
            ) {
                break
            }

            // §14.2 step 12: publish via CAS
            setPushStage('publish-cas')
            const publishPrefetchHints = buildPublishPrefetchHints(uploadSet, mat.root)
            const res = await hooks.rpcPublishIfCurrent(
                snap.expectedRoot,
                snap.expectedVersion,
                mat.root,
                publishPrefetchHints
            )
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            // §14.2 step 13: update remote head from publish response
            setPushStage('apply-publish-result')
            const remoteApplyResult = hooks.applyRemoteHeadIfNewer(
                res.currentRoot,
                res.currentVersion,
                state.remoteRefreshReqSeq
            )
            if (!res.ok) {
                restartNow =
                    remoteApplyResult.kind === 'ADVANCED'
                    || !sameRoot(res.currentRoot, state.workspaceBaseHead.root)
                    || res.currentVersion !== state.workspaceBaseHead.version
                hooks.scheduleSyncRetry()
                break
            }

            // §14.2 step 14: post-publish revalidation (R16 / R50)
            setPushStage('revalidate-post-publish')
            if (
                !revalidatePushPostPublishLocalBaseOrReturn(
                    snap,
                    state.workspaceBaseHead
                )
            ) {
                recordPushAbort(state, snap, 'revalidate-post-publish', true)
                break
            }

            // §14.2 step 15: head advance barrier
            setPushStage('head-advance-drain')
            await hooks.beginHeadAdvancePreparationAndDrain()
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            try {
                // Inline startPendingPushBaseAdvance logic (push.ts)
                if (state.pendingBaseAdvance != null) {
                    throw new Error(INTERNAL_PENDING_BASE_ADVANCE_REENTRY)
                }

                const nextHead: PublicHead = {
                    root: mat.root,
                    version: res.currentVersion,
                }

                state.pendingBaseAdvance = {
                    kind: 'PUSH',
                    intentId: null,
                    promise: (async () => {
                        state.workspaceBaseHead = nextHead
                        const workspacePageCountRef = { value: state.workspacePageCount }
                        subtractPublishedSnapshotFromLiveWorkspaceState(
                            state.workspaceOverlay,
                            workspacePageCountRef,
                            snap.overlaySnapshot,
                            snap.expectedPageCount
                        )
                        state.workspacePageCount = workspacePageCountRef.value
                        await hooks.persistSnapshotSerial(
                            hooks.captureDurableSnapshot(),
                            true
                        )
                    })(),
                }
            } finally {
                hooks.endHeadAdvancePreparation()
            }

            // §14.2 step 16: flush pending base advance
            setPushStage('flush-pending-base-advance')
            if (state.pendingBaseAdvance != null) {
                try {
                    await state.pendingBaseAdvance.promise
                } catch (e) {
                    hooks.enterFatalRuntime(
                        BASE_ADVANCE_FINALIZATION_FAILED,
                        BASE_ADVANCE_FINALIZATION_FAILED
                    )
                    throw e
                } finally {
                    state.pendingBaseAdvance = null
                }
            }
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            // §14.2 step 17: cleanup
            setPushStage('cleanup')
            hooks.gcStagedIfSafe()
            syncCompleted = true
        } while (false)
    } catch (e) {
        restartNow = false
        if (state.syncFatalError == null) {
            const isTransient = hooks.isTransientSyncError(e)
            if (isTransient || isNonFatalSyncError(e)) {
                state.lastSyncError = normalizeSyncError(e)
                if (isTransient) {
                    hooks.scheduleSyncRetry()
                }
            } else {
                hooks.enterFatalRuntime(e, 'PUSH_FATAL')
            }
        }
    } finally {
        if (syncCompleted && state.syncFatalError == null) {
            state.lastSyncError = null
        }
        state.pushSnap = null
        state.pushBusy = false
        setPushStage(null)
    }

    // §14.2 / R66: post-busy epilog — all exit paths converge here
    if (shouldRestartSyncPostBusy(state.syncFatalError, restartNow)) {
        hooks.maybeStartPushOrImport()
    }
}

// ── §15.9 runRemoteReconcile ───────────────────────────────────

export async function runRemoteReconcile(
    state: ControlWorkerRuntimeState,
    hooks: RunReconcileHooks
): Promise<void> {
    const setReconcileStage = (stage: string | null): void => {
        recordDebugStage(state, 'reconcile', stage)
    }
    setReconcileStage('enter')
    if (state.pushBusy || state.mergeBusy || state.syncFatalError != null) return
    if (state.checkpointBusy || state.pendingBaseAdvance != null) return
    if (state.reconcileIntent != null) return

    if (workspaceBaseMatchesRemote(state.remoteHead, state.workspaceBaseHead)) {
        if (
            workspaceHasLocalDelta(
                state.workspaceOverlay,
                state.workspacePageCount,
                state.workspaceBaseHead.root.meta.pageCount
            )
        ) {
            hooks.maybeStartPushOrImport()
        }
        return
    }

    state.mergeBusy = true
    let restartNow = true
    let syncCompleted = false
    let postReconcileCheckpointWanted = false
    let intent: DurableReconcileIntent | null = null
    let preparedIntentNeedsCleanup = false
    let residual: ResidualOverlay | null = null

    const wal0 = hooks.advanceDbHead()
    const overlayPgnos = Array.from(state.workspaceOverlay.keys())
    const maxOverlayPgno = overlayPgnos.length > 0 ? Math.max(...overlayPgnos) : null
    const snap: ReconcileSnapshot = {
        baseRoot: state.workspaceBaseHead.root,
        baseVersion: state.workspaceBaseHead.version,
        basePageCount: state.workspacePageCount,
        remoteRoot: state.remoteHead.root,
        remoteVersion: state.remoteHead.version,
        wal: wal0,
    }
    sdkConsole.trace('[sqlite3mt:engine] reconcile snapshot', {
        baseVersion: snap.baseVersion.toString(),
        baseRootPageCount: snap.baseRoot.meta.pageCount,
        workspacePageCount: state.workspacePageCount,
        remoteVersion: snap.remoteVersion.toString(),
        remoteRootPageCount: snap.remoteRoot.meta.pageCount,
        workspaceOverlaySize: state.workspaceOverlay.size,
        maxOverlayPgno,
        pendingDbDeltaCount: state.pendingDbDeltas.size,
        pendingDbDeltaFrames: Array.from(state.pendingDbDeltas.keys()).slice(-8),
        pendingDbDeltaPageCounts: Array.from(state.pendingDbDeltas.values()).slice(-8).map(delta => delta.postPageCount ?? null),
        integratedFrameMax: state.integratedFrameMax,
        wal: wal0,
    })

    try {
        do {
            setReconcileStage('normalize-local-overlay')
            const localNorm = await hooks.normalizeSparseOverlay(
                snap.baseRoot,
                new Map(state.workspaceOverlay),
                snap.basePageCount
            )
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break
            if (!revalidateReconcileBase(snap, state)) break

            setReconcileStage('build-merge-plan')
            const plan = await hooks.buildMergePlan({
                baseRoot: snap.baseRoot,
                baseVersion: snap.baseVersion,
                remoteRoot: snap.remoteRoot,
                remoteVersion: snap.remoteVersion,
                wal: snap.wal,
                localNorm,
                localPageCount: snap.basePageCount,
                pendingDbDeltasIntegratedFrameMax: state.integratedFrameMax,
            })
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break
            if (!revalidateReconcileBase(snap, state)) break

            setReconcileStage('ensure-metadata-carrier')
            await hooks.ensureMetadataCarrierFrameIfNeeded(plan)
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break
            if (!revalidateReconcileBase(snap, state)) break

            setReconcileStage('build-residual')
            residual = hooks.buildResidualOverlayAgainstRemote(plan)

            intent = {
                intentId: hooks.nextIntentId(),
                phase: 'PREPARED',
                baseRoot: snap.baseRoot,
                baseVersion: snap.baseVersion,
                basePageCount: snap.basePageCount,
                remoteRoot: snap.remoteRoot,
                remoteVersion: snap.remoteVersion,
                walSalt0: plan.walSalt0,
                walSalt1: plan.walSalt1,
                caughtUpFrameMax: plan.caughtUpFrameMax,
                importDelta: buildImportDeltaFromPlan(plan),
                residualOverlay: new Map(residual.pages),
                residualPageCount: residual.pageCount,
                attemptStartFrame: null,
                attemptEndFrame: null,
                attemptWalSalt0: null,
                attemptWalSalt1: null,
                commitFrameMax: null,
                commitPostWalSalt0: null,
                commitPostWalSalt1: null,
            }

            setReconcileStage('ensure-intent-blobs-durable')
            await hooks.ensureReconcileIntentBlobsDurable(plan)
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            setReconcileStage('write-intent-prepared')
            await hooks.writeReconcileIntent(intent)
            hooks.abortIfFatal()
            if (state.syncFatalError != null) break

            state.reconcileIntent = intent
            preparedIntentNeedsCleanup = true

            while (true) {
                setReconcileStage('loop-read-live')
                const live = hooks.advanceDbHead()

                if (!hooks.sameWalSalt(live.salt0, live.salt1, plan.walSalt0, plan.walSalt1)) {
                    break
                }
                if (live.mxFrame < plan.caughtUpFrameMax) {
                    break
                }

                if (live.mxFrame > plan.caughtUpFrameMax) {
                    const effectiveIntegratedFrameMax = reconcileEffectiveIntegratedFrameMax(
                        live,
                        state.integratedFrameMax,
                    )
                    if (live.mxFrame > effectiveIntegratedFrameMax) {
                        if (hooks.reconcileTailCanStillIntegrateSoon()) {
                            setReconcileStage('wait-tail-integrate-soon')
                            await hooks.yieldOneTaskTurnForPendingCommitMessages()
                            hooks.abortIfFatal()
                            if (state.syncFatalError != null) break
                            if (!revalidateReconcileBase(snap, state)) break
                            continue
                        }

                        if (hooks.hasOutstandingCommitPosts()) {
                            setReconcileStage('wait-outstanding-commit-posts')
                            await hooks.awaitOutstandingCommitPostsToChangeOrEnterFatal(
                                RECONCILE_STUCK_OUTSTANDING_COMMIT_POST
                            )
                            hooks.abortIfFatal()
                            if (state.syncFatalError != null) break
                            if (!revalidateReconcileBase(snap, state)) break
                            continue
                        }

                        setReconcileStage('probe-orphan-tail')
                        const probe0 = hooks.reconcileOrphanProbeState(live)
                        await hooks.yieldOneTaskTurnForPendingCommitMessages()
                        hooks.abortIfFatal()
                        if (state.syncFatalError != null) break
                        if (!revalidateReconcileBase(snap, state)) break

                        if (hooks.reconcileTailCanStillIntegrateSoon()) {
                            continue
                        }

                        const live1 = hooks.advanceDbHead()
                        if (!hooks.sameWalSalt(
                            live1.salt0,
                            live1.salt1,
                            probe0.salt0,
                            probe0.salt1
                        )) {
                            continue
                        }

                        const probe1 = hooks.reconcileOrphanProbeState(live1)
                        if (!hooks.sameReconcileOrphanProbeState(probe0, probe1)) {
                            continue
                        }

                        hooks.enterFatalRuntime(
                            RECONCILE_ORPHANED_UNINTEGRATED_WAL_TAIL,
                            RECONCILE_ORPHANED_UNINTEGRATED_WAL_TAIL
                        )
                        break
                    }

                    setReconcileStage('catch-up-merge-plan')
                    await hooks.catchUpMergePlanToFrame(plan, live.mxFrame)
                    hooks.abortIfFatal()
                    if (state.syncFatalError != null) break
                    if (!revalidateReconcileBase(snap, state)) break

                    setReconcileStage('rewrite-metadata-carrier')
                    await hooks.ensureMetadataCarrierFrameIfNeeded(plan)
                    hooks.abortIfFatal()
                    if (state.syncFatalError != null) break
                    if (!revalidateReconcileBase(snap, state)) break

                    setReconcileStage('rebuild-residual')
                    residual = hooks.buildResidualOverlayAgainstRemote(plan)

                    intent.caughtUpFrameMax = plan.caughtUpFrameMax
                    intent.importDelta = buildImportDeltaFromPlan(plan)
                    intent.residualOverlay = new Map(residual.pages)
                    intent.residualPageCount = residual.pageCount

                    setReconcileStage('rewrite-intent-blobs-durable')
                    await hooks.ensureReconcileIntentBlobsDurable(plan)
                    hooks.abortIfFatal()
                    if (state.syncFatalError != null) break

                    setReconcileStage('rewrite-intent-prepared')
                    await hooks.writeReconcileIntent(intent)
                    hooks.abortIfFatal()
                    if (state.syncFatalError != null) break
                }

                setReconcileStage('try-import')
                const importResult = await hooks.tryImportPreparedIntentAsSingleWalCommit({
                    intent,
                    plan,
                })
                hooks.abortIfFatal()
                if (state.syncFatalError != null) break

                if (importResult.kind === 'LOCK_BUSY') {
                    setReconcileStage('try-import-lock-busy')
                    restartNow = false
                    hooks.scheduleSyncRetry()
                    break
                }
                if (importResult.kind === 'WAL_CAPACITY_BUSY') {
                    setReconcileStage('try-import-wal-capacity-busy')
                    sdkConsole.warn('[sqlite3mt:engine] reconcile import waiting for checkpoint capacity', {
                        currentWalBytes: importResult.currentWalBytes,
                        requiredWalBytes: importResult.requiredWalBytes,
                        maxWalBytes: importResult.maxWalBytes,
                        frameCount: importResult.frameCount,
                    })
                    restartNow = false
                    hooks.maybeRequestCheckpoint('reconcile-import-wal-capacity')
                    hooks.scheduleSyncRetry()
                    break
                }
                if (importResult.kind === 'STALE_TIP_GROW') {
                    setReconcileStage('try-import-stale-tip-grow')
                    setReconcileStage('yield-stale-tip-grow')
                    await hooks.yieldOneTaskTurnForPendingCommitMessages()
                    hooks.abortIfFatal()
                    if (state.syncFatalError != null) break
                    if (!revalidateReconcileBase(snap, state)) break
                    continue
                }
                if (importResult.kind === 'STALE_EPOCH') {
                    setReconcileStage('try-import-stale-epoch')
                    setReconcileStage('yield-stale-epoch')
                    await hooks.yieldOneTaskTurnForPendingCommitMessages()
                    hooks.abortIfFatal()
                    if (state.syncFatalError != null) break
                    restartNow = false
                    hooks.scheduleSyncRetry()
                    break
                }
                if (importResult.kind === 'STALE_RESET') {
                    setReconcileStage('try-import-stale-reset')
                    break
                }

                setReconcileStage('try-import-ok')
                preparedIntentNeedsCleanup = false

                setReconcileStage('head-advance-drain')
                await hooks.beginHeadAdvancePreparationAndDrain()
                hooks.abortIfFatal()
                if (state.syncFatalError != null) break

                try {
                    setReconcileStage('start-pending-base-advance')
                    hooks.startPendingReconcileBaseAdvance(
                        intent,
                        residual ?? hooks.buildResidualOverlayAgainstRemote(plan),
                        importResult.appendMeta
                    )
                } finally {
                    hooks.endHeadAdvancePreparation()
                }

                setReconcileStage('flush-pending-base-advance')
                await hooks.flushPendingBaseAdvanceIfAny()
                hooks.abortIfFatal()
                if (state.syncFatalError != null) break

                state.reconcileIntent = null
                hooks.requestPersistLocalSnapshot()
                postReconcileCheckpointWanted = true
                hooks.gcStagedIfSafe()
                syncCompleted = true
                setReconcileStage('complete')
                break
            }
        } while (false)
    } catch (e) {
        restartNow = false

        if (intent != null && intent.phase === 'APPENDING') {
            hooks.enterUncertainAppendingFatal(hooks.enterFatalRuntime)
            return
        }

        if (state.syncFatalError == null) {
            const isTransient = hooks.isTransientSyncError(e)
            if (isTransient || isNonFatalSyncError(e)) {
                state.lastSyncError = normalizeSyncError(e)
                if (isTransient) {
                    hooks.scheduleSyncRetry()
                }
            } else {
                hooks.enterFatalRuntime(e, 'RECONCILE_FATAL')
            }
        }
    } finally {
        try {
            if (
                preparedIntentNeedsCleanup
                && intent != null
                && intent.phase === 'PREPARED'
                && state.syncFatalError == null
            ) {
                await hooks.abandonPreparedReconcileIntent(intent)
            }
        } finally {
            if (syncCompleted && state.syncFatalError == null) {
                state.lastSyncError = null
            }
            state.mergeBusy = false
            setReconcileStage(null)
        }
    }

    if (postReconcileCheckpointWanted && state.syncFatalError == null) {
        hooks.maybeRequestCheckpoint('post-remote-reconcile')
    }

    if (shouldRestartSyncPostBusy(state.syncFatalError, restartNow)) {
        hooks.maybeStartPushOrImport()
    }
}
